import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commentsApi, Comment } from '@/lib/comments';

/**
 * Inline comment thread for any cell/tile/variance/forecast.
 * Mentions: `@<user-uuid>` (UI picks via a separate component → expands to the UUID).
 */
export function CommentThread({ workspaceId, targetKind, targetId }: {
  workspaceId: string;
  targetKind: string;
  targetId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [body, setBody] = useState('');

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', workspaceId, targetKind, targetId],
    queryFn: () => commentsApi.list(workspaceId, targetKind, targetId),
    refetchInterval: 60_000,
  });

  const create = useMutation({
    mutationFn: () => commentsApi.create(workspaceId, { targetKind, targetId, body }),
    onSuccess: () => {
      setBody('');
      qc.invalidateQueries({ queryKey: ['comments', workspaceId, targetKind, targetId] });
    },
  });

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {comments.length === 0 && <li className="text-xs text-ink-500">{t('variance.comments_empty')}</li>}
        {comments.map((c) => <CommentItem key={c.id} comment={c} />)}
      </ul>

      <div className="flex gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('variance.comment_placeholder')}
          rows={2}
          className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
        />
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending || !body.trim()}
          className="self-start px-3 py-2 rounded-md bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60"
        >
          {t('variance.comment_post')}
        </button>
      </div>
    </div>
  );
}

function CommentItem({ comment }: { comment: Comment }) {
  const { t } = useTranslation();
  return (
    <li className="rounded-lg bg-ink-50 px-3 py-2 text-sm">
      <div className="text-xs text-ink-500 mb-0.5 num">
        {comment.userId?.slice(0, 8) ?? 'unknown'} · {new Date(comment.createdAt).toLocaleString()}
      </div>
      <p className="text-ink-800 leading-relaxed whitespace-pre-wrap">{comment.body}</p>
      {comment.mentions.length > 0 && (
        <p className="mt-1 text-xs text-accent-700">{t('variance.mentions_n', { count: comment.mentions.length })}</p>
      )}
    </li>
  );
}
